import styled from '@emotion/styled'

import { isTruthy } from '@bemlo/utils'

import { Color } from '../../../../constants'
import { AvailableShiftChannels } from '../types'

type Channel = 'PUBLISHED' | 'POOL' | 'TENDERED'

const CHANNEL_COLORS: Record<Channel, string> = {
  PUBLISHED: Color.LIGHT_BLUE,
  POOL: '#FFA800',
  TENDERED: Color.PURPLE,
}

const MAX_BULBS = 3
const BULB_HEIGHT = 9
const VERTICAL_SPACING = -2.5

const StyledSvg = styled.svg`
  display: inline-block;
  vertical-align: middle;
`

type ChannelIndicatorProps = {
  type: Channel
  active: boolean
  position: number
  totalBulbs: number
}

const Bulb = (props: ChannelIndicatorProps) => {
  const color = CHANNEL_COLORS[props.type]

  const totalHeight =
    BULB_HEIGHT * MAX_BULBS + VERTICAL_SPACING * (MAX_BULBS - 1)
  const actualContentHeight =
    BULB_HEIGHT * props.totalBulbs + VERTICAL_SPACING * (props.totalBulbs - 1)
  const verticalOffset = (totalHeight - actualContentHeight) / 2

  // Base position plus centering offset
  const y =
    props.position * (BULB_HEIGHT + VERTICAL_SPACING) +
    0.699219 +
    (props.totalBulbs < MAX_BULBS ? verticalOffset : 0)

  return (
    <>
      <rect
        x="0.613281"
        y={y}
        width="9"
        height="9"
        rx="4.5"
        fill={props.active ? color : 'white'}
        stroke="white"
      />
      <rect
        x="1.613281"
        y={y + 1}
        width="7"
        height="7"
        rx="3.5"
        stroke={color}
      />
    </>
  )
}

export type ChannelIndicatorsProps = {
  available: AvailableShiftChannels
  used: {
    isPublished?: boolean
    isRequested?: boolean
    isTendered?: boolean
  }
  className?: string
}

const isSomeUsed = (used: ChannelIndicatorsProps['used']) => {
  return Object.values(used).some(isTruthy)
}

const getAvailable = ({
  publishing,
  pools,
  tenders,
}: AvailableShiftChannels): Channel[] => {
  return [
    publishing && ('PUBLISHED' as const),
    pools && ('POOL' as const),
    tenders && ('TENDERED' as const),
  ].filter(isTruthy)
}

const enabled: Record<Channel, keyof ChannelIndicatorsProps['used']> = {
  PUBLISHED: 'isPublished',
  POOL: 'isRequested',
  TENDERED: 'isTendered',
}

export const ChannelIndicators = (props: ChannelIndicatorsProps) => {
  if (!isSomeUsed(props.used)) {
    return null
  }

  const channels = getAvailable(props.available)

  if (!channels.length) {
    return null
  }

  return (
    <StyledSvg
      width={11}
      height={23}
      viewBox="0 0 11 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      {channels.map((channel, i) => (
        <Bulb
          key={channel}
          type={channel}
          active={!!props.used[enabled[channel]]}
          position={i}
          totalBulbs={channels.length}
        />
      ))}
    </StyledSvg>
  )
}
